import { ApolloError } from "@apollo/client";
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import React from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  Alert,
  Button,
  Link,
  ZoomAppAutoOpenOrgSetting,
} from "../../../components";
import { DisclaimerFormData } from "../../../components/DisclaimerInputs/DisclaimerInputs";
import NotetakerDisplayImageSelector from "../../../components/NotetakerDisplayImage/NotetakerDisplayImageSelector";
import { ZoomDisclaimerInputs } from "../../../components/ZoomDisclaimerInputs";
// import NotetakerDisplayImageSelector from "../../../components/NotetakerDisplayImage/NotetakerDisplayImageSelector";
import {
  CurrentUserFragment,
  Maybe,
  NotetakerRecorderDisclaimerType,
  OrgZoomAppAutoOpenRule,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import ZoomBotRecordingAuth from "./ZoomBotRecordingAuth";

export interface FormData extends DisclaimerFormData {
  enabled: boolean;
  zoomSendCancelRecordingChatMessage: boolean;
  virtualOnsiteEnabled: boolean;
  displayName?: Maybe<string>;
  displayImage?: string;
  displayImageFilename?: string;
  zoomAppAutoOpenRule: OrgZoomAppAutoOpenRule;
  useNativeNotetakerBot: boolean;
}

interface ZoomNotetakerSettingsFormProps {
  organizationName: string;
  currentUser: CurrentUserFragment;
  zoomNotetakerEnabled: boolean;
  zoomSendCancelRecordingChatMessage: boolean;
  zoomAppAutoOpenRule?: Maybe<OrgZoomAppAutoOpenRule>;
  zoomNotetakerDisclaimer?: Maybe<string>;
  zoomNotetakerDisclaimerType: NotetakerRecorderDisclaimerType;
  zoomNotetakerDisplayImageBucketPath?: Maybe<string>;
  defaultDisclaimer: string;
  defaultDisplayName?: Maybe<string>;
  virtualOnsiteEnabled: boolean;
  showVirtualOnsiteSetting: boolean;
  useNativeNotetakerBot: boolean;
  error: ApolloError | undefined;
  isLoading: boolean;
  onSubmit: (formData: FormData) => Promise<void>;
}

const ZoomNotetakerSettingsForm: React.FC<ZoomNotetakerSettingsFormProps> = ({
  organizationName,
  currentUser,
  zoomNotetakerEnabled,
  zoomSendCancelRecordingChatMessage,
  zoomAppAutoOpenRule,
  zoomNotetakerDisclaimer,
  zoomNotetakerDisclaimerType,
  zoomNotetakerDisplayImageBucketPath,
  defaultDisclaimer,
  defaultDisplayName,
  virtualOnsiteEnabled,
  showVirtualOnsiteSetting,
  useNativeNotetakerBot,
  error,
  isLoading,
  onSubmit,
}) => {
  const zoomAppAutoOpenEnabled = useFeatureFlag("zoom-app-auto-open");
  const disableChatDisclaimer = useFeatureFlag("disable-chat-disclaimer");
  const defaultValues: FormData = {
    enabled: zoomNotetakerEnabled,
    zoomSendCancelRecordingChatMessage,
    zoomAppAutoOpenRule: zoomAppAutoOpenRule ?? OrgZoomAppAutoOpenRule.Disabled,
    disclaimer: zoomNotetakerDisclaimer ?? "",
    disclaimerType: disableChatDisclaimer
      ? NotetakerRecorderDisclaimerType.Native
      : zoomNotetakerDisclaimerType,
    defaultDisclaimer,
    virtualOnsiteEnabled,
    displayName: defaultDisplayName,
    useNativeNotetakerBot,
  };

  const formMethods = useForm<FormData>({
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = formMethods;

  const DEFAULT_BH_DISPLAY_IMAGE = "brighthire.jpg";
  const previewImageSrc =
    zoomNotetakerDisplayImageBucketPath ?? DEFAULT_BH_DISPLAY_IMAGE;
  const [displayImageBase64, setDisplayImageBase64] = React.useState<
    string | null
  >(null);

  const notetakerDisclaimerType = disableChatDisclaimer
    ? NotetakerRecorderDisclaimerType.Native
    : watch("disclaimerType");

  return (
    <FormProvider {...formMethods}>
      <form
        autoComplete="on"
        onSubmit={handleSubmit(async (formData) => {
          await onSubmit({
            ...formData,
            ...(displayImageBase64 && { displayImage: displayImageBase64 }),
          });
          setDisplayImageBase64(null);
        })}
      >
        <Tabs>
          <TabList>
            <Tab>Configuration</Tab>
            <Tab>Customization</Tab>
            <Tab>Other Options</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              {error?.graphQLErrors?.map(({ message }, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Alert mb={8} key={i} status="error" description={message} />
              ))}
              {/** Primary fields */}
              <FormControl id="zoomNotetakerEnabled" mb={4} mt={4}>
                <Checkbox
                  {...register("enabled")}
                  defaultChecked={zoomNotetakerEnabled}
                >
                  Enabled
                </Checkbox>
                <FormHelperText>
                  This turns on the Zoom Notetaker for your organization.
                </FormHelperText>
              </FormControl>

              {!disableChatDisclaimer && <ZoomDisclaimerInputs />}

              {notetakerDisclaimerType ===
                NotetakerRecorderDisclaimerType.Native && (
                <FormControl id="zoomAppSetup" mb={4} mt={8}>
                  <Box mb={4} mt={4}>
                    <ZoomBotRecordingAuth currentUser={currentUser} />
                  </Box>
                  <FormHelperText>
                    Installing the BrightHire Notetaker Zoom App will allow
                    BrightHire to record interviews without asking the Zoom Host
                    for permission. This setting is required to use the 3rd
                    party recording option.
                  </FormHelperText>
                </FormControl>
              )}
            </TabPanel>
            <TabPanel>
              <FormControl
                id="displayName"
                mb={4}
                mt={4}
                isInvalid={errors.displayName !== undefined}
              >
                <FormLabel fontSize="md">Notetaker display name</FormLabel>
                <Input
                  {...register("displayName")}
                  placeholder={`${organizationName} Notetaker`}
                  data-testid="display-name"
                  maxLength={60}
                />
                <FormHelperText>
                  The name that will be displayed for the Zoom Notetaker in
                  meetings.
                </FormHelperText>
                {errors.displayName !== undefined && (
                  <FormErrorMessage>
                    {errors.displayName.message}
                  </FormErrorMessage>
                )}
              </FormControl>

              <NotetakerDisplayImageSelector
                previewImageSrc={previewImageSrc}
                onResetToDefault={() => {
                  handleSubmit(async (formData) => {
                    await onSubmit({
                      ...formData,
                      ...{
                        displayImageFilename: DEFAULT_BH_DISPLAY_IMAGE,
                      },
                    });
                  })();
                  setDisplayImageBase64(null);
                }}
                onImageGenerated={setDisplayImageBase64}
                onImageReset={() => setDisplayImageBase64(null)}
                showResetToDefault={
                  !previewImageSrc.endsWith(DEFAULT_BH_DISPLAY_IMAGE)
                }
              />
            </TabPanel>
            <TabPanel>
              <FormControl mt={4}>
                <Checkbox
                  {...register("zoomSendCancelRecordingChatMessage")}
                  defaultChecked={zoomSendCancelRecordingChatMessage}
                >
                  Send Cancel Recording Link
                </Checkbox>
                <FormHelperText>
                  When each participant joins, the Notetaker will send them a
                  message which contains a link that when clicked will remove
                  the Notetaker and delete the recording.
                </FormHelperText>
              </FormControl>

              {zoomAppAutoOpenEnabled && (
                <ZoomAppAutoOpenOrgSetting
                  id="zoomAppAutoOpenRule"
                  mt={4}
                  register={register("zoomAppAutoOpenRule")}
                />
              )}

              <Box
                mb={6}
                hidden={!zoomNotetakerEnabled || !showVirtualOnsiteSetting}
              >
                <Divider mt={4} />
                <Heading as="h4" fontSize="md" mt={4}>
                  Virtual Onsite
                </Heading>

                <FormControl mt={4} id="virtualOnsiteEnabled">
                  <Checkbox
                    {...register("virtualOnsiteEnabled")}
                    defaultChecked={virtualOnsiteEnabled}
                    data-testid="virtual-onsite-enabled"
                  >
                    Enabled
                  </Checkbox>
                  <FormHelperText>
                    Use interviewer attendance to automatically create separate
                    interview recordings of sequential scheduled interviews that
                    share a meeting meeting url. This will also provide
                    interviewers more control over joining, starting, stopping
                    the different sections of onsite via the Interview
                    Assistant.
                  </FormHelperText>
                  <FormHelperText>
                    <Link
                      href="http://help.brighthire.ai/en/articles/6954308-brighthire-and-virtual-onsites"
                      target="_blank"
                    >
                      {" Learn More"}
                    </Link>
                  </FormHelperText>
                </FormControl>
              </Box>
            </TabPanel>
          </TabPanels>
        </Tabs>
        <Button
          type="submit"
          mt={4}
          isLoading={isLoading}
          data-testid="zoom-notetaker-settings-submit"
        >
          Save
        </Button>
      </form>
    </FormProvider>
  );
};

export default ZoomNotetakerSettingsForm;
